import { allMentions, entityName, imageIdOf, isScene, orderedGroups, plainRefs, type Entity, type LibraryGroup, type MentionHit, type NodeType, type StoryCtx } from '@dispositio/core';
import { categoryOf, groupsOfType, layoutLibrary } from './libraryModel';

/**
 * Modus „Bibliothek“: Bereiche der linken Leiste (Personen, Orte, Landschaften, Gegenstände, Organisationen, Konzepte, Welt und eigene Klassen) und
 * die Karten ihrer Bibliothek. Reine Berechnung.
 */

export type FocusKey = 'person' | 'place' | 'landscape' | 'object' | 'organization' | 'concept' | 'world' | `type:${string}`;

export interface Focus {
  key: FocusKey;
  label: string;
  /** Klasse des Bereichs (fehlt bei älteren Projekten, bis man ihn zum ersten Mal benutzt; bei „Welt“ null) */
  type: NodeType | null;
  /** Name der vordefinierten Klasse (für Vorlage und Symbol) */
  builtinName: string | null;
  count: number;
  color: string;
}

const BUILTIN_FOCUS: { key: FocusKey; label: string; typeName: string }[] = [
  { key: 'person', label: 'Personen', typeName: 'Person' },
  { key: 'place', label: 'Orte', typeName: 'Ort' },
  { key: 'landscape', label: 'Landschaften', typeName: 'Landschaft' },
  { key: 'object', label: 'Gegenstände', typeName: 'Gegenstand' },
  { key: 'organization', label: 'Organisationen', typeName: 'Organisation' },
  { key: 'concept', label: 'Konzepte', typeName: 'Konzept' },
];

/** Die Bereiche der linken Leiste in fester Reihenfolge, eigene Klassen (Objekt, Ort) danach, „Welt“ am Ende. */
export function designFoci(ctx: StoryCtx): Focus[] {
  const count = (typeId: string | undefined): number => (typeId ? ctx.story.entities.filter((e) => e.typeId === typeId).length : 0);
  const builtin = BUILTIN_FOCUS.map((f): Focus => {
    const type = ctx.story.types.find((t) => t.builtin && t.name === f.typeName && (t.category === 'object' || t.category === 'place')) ?? null;
    return { key: f.key, label: f.label, type, builtinName: f.typeName, count: count(type?.id), color: type?.color ?? '#a39a90' };
  });
  const own = ctx.story.types
    .filter((t) => !t.builtin && (t.category === 'object' || t.category === 'place'))
    .map((t): Focus => ({ key: `type:${t.id}`, label: t.name, type: t, builtinName: null, count: count(t.id), color: t.color }));
  const world = ctx.story.types.find((t) => t.category === 'foundational' && t.name === 'Welt');
  return [...builtin, ...own, { key: 'world', label: 'Welt', type: null, builtinName: 'Welt', count: 0, color: world?.color ?? '#4a8796' }];
}

/**
 * Bereich zu einer Klasse, auch einer anderen Geschichte (Import): vordefinierte nach Namen („Personen“ mit ihrem Symbol), eigene unter
 * ihrem Namen. `order` sortiert wie die linke Leiste.
 */
export function classFocus(c: { id: string; name: string; builtin: boolean; color: string }): Pick<Focus, 'key' | 'label' | 'color'> & { order: number } {
  const i = c.builtin ? BUILTIN_FOCUS.findIndex((f) => f.typeName === c.name) : -1;
  const b = BUILTIN_FOCUS[i];
  return b ? { key: b.key, label: b.label, color: c.color, order: i } : { key: `type:${c.id}`, label: c.name, color: c.color, order: BUILTIN_FOCUS.length };
}

export interface EntityCard {
  entity: Entity;
  title: string;
  text: string;
  imageId: string | null;
  /** In wie vielen Szenen der Eintrag vorkommt (in ihr, als Perspektive oder mit Verweis in ihren Texten) */
  scenes: number;
}

/** Szene, zu der ein Node gehört (er selbst oder der nächste Vorfahr, der eine Szene ist). */
function sceneOf(ctx: StoryCtx, nodeId: string): string | null {
  for (let cur = ctx.nodes.get(nodeId); cur; cur = cur.parentId ? ctx.nodes.get(cur.parentId) : undefined) if (isScene(cur)) return cur.id;
  return null;
}

/** In wie vielen Szenen ein Eintrag vorkommt: als Kopie in ihr, als Perspektive bzw. Schauplatz oder mit einem Verweis in ihren Texten. */
export function entityUsage(ctx: StoryCtx, entityId: string, mentions: MentionHit[] = allMentions(ctx)): { scenes: number } {
  const scenes = new Set(
    [
      ...ctx.story.nodes.filter((n) => n.entityId === entityId).map((n) => sceneOf(ctx, n.id)),
      ...mentions.filter((m) => m.entityId === entityId).map((m) => sceneOf(ctx, m.nodeId)),
      ...ctx.story.nodes.filter((n) => isScene(n) && (n.fields.pov === entityId || n.fields.place === entityId)).map((n) => n.id),
    ].filter((x): x is string => x !== null),
  );
  return { scenes: scenes.size };
}

/** Karten der Einträge einer Klasse, nach Titel sortiert und nach `query` gefiltert (Titel, Schlüssel, Kurzbeschreibung). */
export function entityCards(ctx: StoryCtx, typeId: string, query = ''): EntityCard[] {
  const type = ctx.types.get(typeId);
  if (!type) return [];
  const mentions = allMentions(ctx);
  const q = query.trim().toLowerCase();
  return ctx.story.entities
    .filter((e) => e.typeId === typeId)
    .map((entity): EntityCard => {
      const title = entityName(entity, type) || type.name;
      const text = type.hoverKey ? plainRefs(String(entity.fields[type.hoverKey] ?? '').trim()) : '';
      return { entity, title, text, imageId: imageIdOf(type, entity.fields, entity.colorSlot, entity.id), ...entityUsage(ctx, entity.id, mentions) };
    })
    .filter((c) => q === '' || c.title.toLowerCase().includes(q) || c.text.toLowerCase().includes(q) || [c.entity.refKey, ...(c.entity.aliases ?? [])].some((k) => k.includes(q)))
    .sort((a, b) => a.title.localeCompare(b.title, 'de'));
}

/** Karten eines Bereichs nach Kategorien (Rahmen der Pinnwand, in der gewählten Reihenfolge) und die Karten ohne Kategorie. */
export function categorySections(ctx: StoryCtx, typeId: string, query = ''): { categories: { group: LibraryGroup; cards: EntityCard[] }[]; rest: EntityCard[] } {
  const groups = orderedGroups(groupsOfType(ctx, typeId));
  const pos = new Map(layoutLibrary(ctx).map((p) => [p.entity.id, p]));
  const byGroup = new Map<string, EntityCard[]>(groups.map((g) => [g.id, []]));
  const rest: EntityCard[] = [];
  for (const card of entityCards(ctx, typeId, query)) {
    const p = pos.get(card.entity.id);
    const g = p ? categoryOf(groups, p) : null;
    if (g) byGroup.get(g.id)!.push(card);
    else rest.push(card);
  }
  return { categories: groups.map((group) => ({ group, cards: byGroup.get(group.id)! })), rest };
}

/** Kategorie eines Eintrags (Rahmen, in dem seine Karte auf der Pinnwand liegt), sonst null. */
export function categoryOfEntity(ctx: StoryCtx, entityId: string): LibraryGroup | null {
  const e = ctx.entities.get(entityId);
  const p = layoutLibrary(ctx).find((x) => x.entity.id === entityId);
  return e && p ? categoryOf(groupsOfType(ctx, e.typeId), p) : null;
}

/** Bereich, zu dem ein Eintrag gehört (für „Kommt vor“ und Sprünge in die Bibliothek). */
export function focusOfEntity(ctx: StoryCtx, entityId: string): FocusKey {
  const e = ctx.entities.get(entityId);
  const type = e ? ctx.types.get(e.typeId) : undefined;
  if (!type) return 'person';
  if (type.category === 'foundational') return 'world';
  return designFoci(ctx).find((f) => f.type?.id === type.id)?.key ?? 'person';
}

/** Titel eines Eintrags für Anzeigen (Titelfeld, sonst Klassenname). */
export const entityLabel = (ctx: StoryCtx, e: Entity): string => {
  const type = ctx.types.get(e.typeId);
  return entityName(e, type) || type?.name || e.refKey;
};

